import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import { PayloadAction } from "@reduxjs/toolkit";
import { compareKeys, generateAmazonClaimReferenceId } from "src/forms/tools";
import { ClaimSubmittedStatus } from "../types";
import {
  IntlProductLiabilityClaimSummary,
  ListInternationalProductLiabilityClaimsInput,
  ListInternationalProductLiabilityClaimsOutput,
  CreateInternationalProductLiabilityClaimCommandInput,
} from "@amzn/ttechclaimintakeservice-client";
import { listIPLClaimsService } from "src/api/IPL/listIPLClaims";
import { createIPLClaimService } from "src/api/IPL/createIPLClaim";
import {
  ProcessedApiResponse,
  ResponseField,
  SERVER_ERROR_RESPONSES,
} from "src/api/types";
import { IPL_TPA_SEDGWICK } from "src/forms/IPL/constants";

interface IPLAppState {
  [index: string]: any;
  submitScreenStatus: ClaimSubmittedStatus;
  iplClaimsList: IntlProductLiabilityClaimSummary[];
  fetchingClaimsList: boolean;
  listIPLClaimsError: string;
  createIPLClaimResponse?: ProcessedApiResponse;
  amazonClaimReferenceId: string;
  iplTpa: string;
}

export const initialState: IPLAppState = {
  submitScreenStatus: ClaimSubmittedStatus.LOADING,
  iplClaimsList: [],
  fetchingClaimsList: false,
  listIPLClaimsError: "",
  amazonClaimReferenceId: generateAmazonClaimReferenceId(),
  iplTpa: IPL_TPA_SEDGWICK,
};

export const verifyStateIsValid = (state: IPLAppState) => {
  return compareKeys(state, initialState);
};

export const listIPLClaims = createAsyncThunk(
  "iplApp/listIPLClaims",
  async (
    params: { input: ListInternationalProductLiabilityClaimsInput },
    thunkAPI: any
  ) => {
    const { input } = params;
    try {
      const response = await listIPLClaimsService(input);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const createIPLClaim = createAsyncThunk(
  "iplApp/createIPLClaim",
  async (
    params: {
      createIPLClaimInput: CreateInternationalProductLiabilityClaimCommandInput;
    },
    thunkAPI: any
  ) => {
    const { createIPLClaimInput } = params;
    try {
      const response = await createIPLClaimService(createIPLClaimInput);
      return response;
    } catch (e) {
      return thunkAPI.rejectWithValue(e);
    }
  }
);

export const iplAppSlice = createSlice({
  name: "iplApp",
  initialState,
  reducers: {
    setValueInIPLAppState: (
      state: IPLAppState,
      action: PayloadAction<{
        key: string;
        value: any;
      }>
    ) => {
      const { key, value } = action.payload;
      state[key as keyof IPLAppState] = value;
    },
    resetIPLAppState: () => {
      return {
        ...initialState,
        amazonClaimReferenceId: generateAmazonClaimReferenceId(),
      };
    },
  },
  extraReducers: (builder: any) => {
    builder
      .addCase(
        listIPLClaims.fulfilled,
        (
          state: IPLAppState,
          action: PayloadAction<ListInternationalProductLiabilityClaimsOutput>
        ) => {
          state.iplClaimsList = action.payload.claimsList ?? [];
          state.fetchingClaimsList = false;
          state.listIPLClaimsError = "";
        }
      )
      .addCase(listIPLClaims.pending, (state: IPLAppState) => {
        state.fetchingClaimsList = true;
        state.listIPLClaimsError = "";
        state.iplClaimsList = [];
      })
      .addCase(listIPLClaims.rejected, (state: IPLAppState) => {
        state.fetchingClaimsList = false;
        state.listIPLClaimsError = "Error fetching IPL claims list";
        state.iplClaimsList = [];
      })
      .addCase(
        createIPLClaim.fulfilled,
        (state: IPLAppState, action: PayloadAction<any>) => {
          state.createIPLClaimResponse = action.payload;
          state.submitScreenStatus = ClaimSubmittedStatus.SUCCESS;
        }
      )
      .addCase(createIPLClaim.pending, (state: IPLAppState) => {
        state.submitScreenStatus = ClaimSubmittedStatus.LOADING;
      })
      .addCase(
        createIPLClaim.rejected,
        (state: IPLAppState, action: PayloadAction<any>) => {
          state.submitScreenStatus = ClaimSubmittedStatus.ERROR;
          const data: ResponseField[] = [];
          const { message, errors, $metadata, warningList } = action.payload;
          const statusCode = $metadata?.httpStatusCode?.toString();
          if (SERVER_ERROR_RESPONSES.includes(statusCode)) {
            data.push({
              fieldName: "Amazon Claim Reference ID",
              fieldValue: current(state).amazonClaimReferenceId,
            });
          }

          if (errors?.length) {
            data.push(...errors);
          }
          const processedResponse: ProcessedApiResponse = {
            data,
            statusCode,
            warningList,
            message,
          };
          state.createIPLClaimResponse = processedResponse;
        }
      );
  },
});

export const { setValueInIPLAppState, resetIPLAppState } = iplAppSlice.actions;

export default iplAppSlice.reducer;
